import { useContext, useEffect, useRef } from "react";
import axios from "axios";

import {
  ProjectDetailContext,
  UseSnackbarContext,
} from "./Interfaces/ProjectDetailInterfaces";
import { config } from "../../config/config";
import { SCENE_TYPE } from "../../viewer/config/ViewerConfig";
import {
  attachGsplatLayer,
  createServerTransport,
  GsplatLayerHandle,
} from "../../viewer/gaussian-splatting";

const GaussianSplattingViewer = () => {
  const { projectDetail, viewer } = useContext(ProjectDetailContext);
  const { openError } = useContext(UseSnackbarContext);
  const handleRef = useRef<GsplatLayerHandle | null>(null);

  useEffect(() => {
    if (!projectDetail?.id || !projectDetail.gsx_file) return;

    console.log(
      `[gsplat] mounting GaussianSplattingViewer for project=${projectDetail.id} gsx_file=${projectDetail.gsx_file}`,
    );

    let aborted = false;
    const ac = new AbortController();

    viewer.gsplatStatus = "loading";

    (async () => {
      try {
        console.log("[gsplat] fetching jobId via /by-project ...");
        const res = await axios.get(
          `${config.serverHost}/api/v1/convert/gsplat/by-project/${projectDetail.id}`,
          { withCredentials: true, signal: ac.signal },
        );
        if (aborted) return;
        const jobId = res.data?.jobId as string | undefined;
        console.log(
          `[gsplat] /by-project response status=${res.data?.status} jobId=${jobId} outputKey=${res.data?.outputKey} splatCount=${res.data?.splatCount} blockCount=${res.data?.blockCount}`,
        );
        if (!jobId) {
          viewer.gsplatStatus = "error";
          openError("Gaussian Splatting data not ready yet");
          return;
        }

        const outputKey = res.data?.outputKey as string | undefined;
        if (!outputKey) {
          viewer.gsplatStatus = "error";
          openError("Gaussian Splatting output file not found");
          return;
        }
        const transport = createServerTransport({
          fileUrl: `${config.serverHost}/${outputKey}`,
          jobId,
          withCredentials: true,
        });

        console.log(
          `[gsplat] attaching layer (renderer + STREAM_POINTCLOUD scene + viewer camera) ...`,
        );
        handleRef.current = attachGsplatLayer({
          jobId,
          transport,
          renderer: viewer.getRenderer(),
          scene: viewer.getScene(SCENE_TYPE.STREAM_POINTCLOUD),
          camera: viewer.getCamera(),
          // onStats omitted — fires per block load and per frame (spammy).
          // Re-enable per-need if debugging loader / culling.
          // The viewer render loop is on-demand (gated by _renderLoopCount).
          // Async setup + block loads must request a frame, else the splat
          // stays invisible until the user interacts. Reuse the same
          // updateRenderer the viewer wires GaspManager.onNeedRender to.
          onRenderNeeded: () => viewer.updateRenderer(2),
          onLoadError: (e) => {
            console.error(`[gsplat] load error stage=${e.stage}: ${e.message}`);
            // openError(`Gaussian Splatting load failed: ${e.message}`);
          },
        });
        viewer.gsplatStatus = "loaded";
        console.log(`[gsplat] layer attached, jobId=${jobId}`);
      } catch (err: any) {
        if (axios.isCancel(err) || err?.name === "CanceledError") return;
        viewer.gsplatStatus = "error";
        console.error("[gsplat] fetch jobId failed", err);
        const status = err?.response?.status;
        openError(
          status === 404
            ? "Gaussian Splatting conversion not yet completed"
            : "Failed to load Gaussian Splatting data",
        );
      }
    })();

    return () => {
      console.log("[gsplat] unmounting GaussianSplattingViewer");
      aborted = true;
      ac.abort();
      handleRef.current?.dispose();
      handleRef.current = null;
      viewer.gsplatStatus = "blank";
    };
  }, [projectDetail?.id, projectDetail?.gsx_file]);

  return null;
};

export default GaussianSplattingViewer;
